// a collection of functions relating to the llm agent tools
// also update the tools in /tt/api/chat in /tt/api/chat/+server.ts
// also update the ChatMessage component in /tt/components/ChatMessage.svelte
export function defaultToolsStrings() {
  return [
    'my_documents',
    'my_documents_by_title',
    'save_document',
    'phone_a_friend',
    'ib_extras_knowledge_base',
    'web_search',
    'ib_essentials_knowledge_base',
    'common_core_knowledge_base',
    'udl_knowledge_base'
  ];
}

export interface ToolDisplayInfo {
  name: string;
  description: string;
  about: string;
  status: "ga" | "beta" | "alpha" | "deprecated" | "experimental";
  key: string;
  hidden: boolean;
  icon: string; // New attribute for the emoji icon
}

export function toolsInfoObjects(options: {
  filterHidden?: boolean;
}) {
  // TODO provide a parameter to return tools by array of keys
  let toolsList: ToolDisplayInfo[] = [
    // v1 tools
    {
      name: '📄 Search Docs by Title',
      description: 'Search saved documents by title.',
      about: 'This tool searches your saved documents using keywords on the title, for fast retrieval of known documents.',
      status: 'ga',
      key: 'my_documents_by_title',
      hidden: true,
      icon: '📄'
    },
    {
      name: '📄 Search Docs',
      description: 'Search saved documents by title or contents.',
      about: 'This tool searches your saved documents\' content using a combination of keywords and conceptual similarity (dense vector search) to find the most relevant results.',
      status: 'ga',
      key: 'my_documents',
      hidden: false,
      icon: '📄'
    },
    {
      name: '💾 Save Document',
      description: 'Save a document to the user library.',
      about: 'This tool saves a new document to your library, including a generated title and markdown content, for easy retrieval later.',
      status: 'ga',
      key: 'save_document',
      hidden: false,
      icon: '💾'
    },
    {
      name: '🧑‍🏫 Phone a Friend',
      description: 'Call another TT to perform a task.',
      about: 'This experimental tool allows your TeamTeacher to make a request to another TeamTeacher to perform a specific task. This is a work in progress and may not work as expected.',
      status: 'experimental',
      key: 'phone_a_friend',
      hidden: false,
      icon: '🧑‍🏫'
    },
    {
      name: '🔎 Search the Web',
      description: 'Search the web.',
      about: 'This tool searches the web using an AI-friendly search engine to find information from the internet. It is particularly well-suited for searching news articles. You can specify the sources youw want searched.',
      status: 'ga',
      key: 'web_search',
      hidden: false,
      icon: '🔎'
    },
    {
      name: '📝 Edit Notes',
      description: 'Edit the conversation notes.',
      about: 'Tool to take and edit notes throughout the conversation. This helps the TeamTeacher stay on task and keep track of work done and other notes about the conversation. It acts as a shared scratchpad for you and the TT.',
      status: 'experimental',
      key: 'edit_notes',
      hidden: false,
      icon: '📝'
    },
    {
      name: '📚 IB MYP',
      description: 'Search the MYP KB.',
      about: 'This tool searches the TeamTeacher IB Knowledge Base for essential information about the Middle Years Programme. It includes a curated collection of information derived from the official IB documents.',
      status: 'ga',
      key: 'ib_myp_knowledge_base',
      hidden: false,
      icon: '📚'
    },
    {
      name: '📚 IB DP',
      description: 'Search the DP KB.',
      about: 'This tool searches the TeamTeacher IB Knowledge Base for additional information about the IB Diploma Programme. It includes a curated collection of information derived from the official IB documents and other sources.',
      status: 'ga',
      key: 'ib_dp_knowledge_base',
      hidden: false,
      icon: '📚'
    },
    {
      name: '📚 IB DP & MYP Essentials',
      description: 'Search the TT IB KB.',
      about: 'This tool searches the TeamTeacher IB Knowledge Base for essential information about the IB Diploma Programme and Middle Years Programme. It includes a curated collection of information derived from the official IB documents.',
      status: 'deprecated',
      key: 'ib_essentials_knowledge_base',
      hidden: true,
      icon: '📚'
    },
    {
      name: '📚 IB DP Extras',
      description: 'Search the TT IB Extras KB.',
      about: 'This tool searches the TeamTeacher IB Knowledge Base for additional information about the IB Diploma Programme. It includes a curated collection of information derived from the official IB documents and other sources.',
      status: 'deprecated',
      key: 'ib_extras_knowledge_base',
      hidden: true,
      icon: '📚'
    },
    {
      name: '📚 Common Core (CCSS)',
      description: 'Search the Common Core KB.',
      about: 'This tool searches the Common Core Knowledge Base for information about the US Common Core State Standards. It includes a curated collection of information from the official Common Core documents.',
      status: 'ga',
      key: 'common_core_knowledge_base',
      hidden: false,
      icon: '📚'
    },
    {
      name: '📚 UDL',
      description: 'Search the UDL KB.',
      about: 'This tool searches the Universal Design for Learning Knowledge Base for information about UDL principles and practices. It includes a curated collection of information from the official UDL documents.',
      status: 'ga',
      key: 'udl_knowledge_base',
      hidden: false,
      icon: '📚'
    },
    {
      name: '📚 TX: TEKS & STAAR',
      description: 'Search the Texas State Standards KB.',
      about: 'This tool searches the Texas Knowledge Base for information about the Texas Essential Knowledge and Skills (TEKS) and the State of Texas Assessments of Academic Readiness (STAAR). It includes a curated collection of information from the official Texas documents.',
      status: 'experimental',
      key: 'texas_knowledge_base',
      hidden: false,
      icon: '📚'
    },
    {
      name: '📚 Cook Islands',
      description: 'Search the Cook Islands KB.',
      about: 'This tool searches the Cook Islands Knowledge Base for information about the Cook Islands curriculum. It includes a curated collection of information from the official Cook Islands and New Zealand Ministry of Education documents.',
      status: 'experimental',
      key: 'cook_islands_knowledge_base',
      hidden: false,
      icon: '📚'
    },
    // v0 tools
    { name: '📄 Search my Documents', description: 'Search saved documents.', about: '', status: 'deprecated', key: 'doc', hidden: true, icon: '📄' },
    { name: '🔎 Search the Web', description: 'Search the web.', about: '', status: 'deprecated', key: 'tavily', hidden: true, icon: '🔎' },
    { name: '📚 IB DP & MYP Essentials', description: 'Search the TT IB KB.', about: '', status: 'deprecated', key: 'knowledgeBaseSearch', hidden: true, icon: '📚' },
    { name: '📚 IB DP Extras', description: 'Search the TT IB KB.', about: '', status: 'deprecated', key: 'kb', hidden: true, icon: '📚' },
    { name: '📚 US Common Core', description: 'Search the Common Core KB.', about: '', status: 'deprecated', key: 'commonCoreSearch', hidden: true, icon: '📚' },
    { name: '📚 Universal Design for Learning', description: 'Search the UDL KB.', about: '', status: 'deprecated', key: 'udlSearch', hidden: true, icon: '📚' },
  ];
  if (options.filterHidden === true) {
    return toolsList.filter((tool) => !tool.hidden);
  }
  return toolsList;
}